import BillingDetailsCard from "v2/pages/billing/billing-details/components/BillingDetailsCard";
import CardStack from "components/CardStack";
import SubmitButton from "components/SubmitButton";

import { usePlanHelpers } from "routes/data-routes/billing-data/plans/usePlanHelpers";
import { useUser } from "routes/dataroutes/UserData";
import { useToasterFromActionData } from "hooks/useToasterFromActionData";

import { useBillingFlow } from "v2/pages/billing/providers/BillingFlowProvider";

import { fmtDate } from "utils";
import { fromEpochSeconds } from "utils/dateUtils";
import { isEnterprise } from "utils/planUtils";
import { trackBillingV2CancelClicked } from "utils/Tracking";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import {
  ActiveSubscriptionDataRoutePath
} from "routes/data-routes/billing-data/active-subscription/route.meta";
import {
  ActiveSubscriptionFormActions
} from "routes/data-routes/billing-data/active-subscription/ActiveSubscriptionFormActions";


function DowngradeSection() {
  const user = useUser();
  const { activeSubscription, activeSubscriptionFetcher } = useActiveSubscription();
  const { getDowngradeTarget, getPlanChangeKind } = usePlanHelpers();
  const { flowType } = useBillingFlow();

  // don't show anything if the fetcher is not ready yet, or if we have a scheduled cancellation
  if ((flowType !== "free") && activeSubscription && activeSubscriptionFetcher && (activeSubscription.scheduledSubscription?.planSlug !== "free")) {

    const changeKind = getPlanChangeKind(activeSubscription.planSlug, activeSubscription.scheduledSubscription?.planSlug);

    if (changeKind === "downgrade") {
      // downgrade scheduled, let users change their minds
      // upgrades can also be scheduled but only in Chargebee, we won't let users cancel those changes
      return (
        <BillingDetailsCard
          title={ "Downgrade to " + activeSubscription.scheduledSubscription?.planName + " scheduled" }
          subtitle={ "Your plan will downgrade to " + activeSubscription.scheduledSubscription?.planName + " on " + fmtDate(fromEpochSeconds(activeSubscription.currentTermEnd)) + "." }
          extra={
            <activeSubscriptionFetcher.Form
              method="POST"
              action={ ActiveSubscriptionDataRoutePath }
            >
              <SubmitButton
                text="Cancel Downgrade"
                size="SM"
                className="button button-secondary"
                disabled={ user?.isBlocked }
                { ...ActiveSubscriptionFormActions.remove_scheduled_changes }
              />
            </activeSubscriptionFetcher.Form>
          }
        />
      );
    }

    const smallerPlan = getDowngradeTarget(activeSubscription?.planSlug);
    return (
      <BillingDetailsCard
        title="Downgrade to a smaller plan"
        subtitle="Not sure the current plan fits your needs? Choose a lighter plan that keeps your key features."
        extra={
          (isEnterprise(activeSubscription?.planSlug) || !smallerPlan) ?
            <SubmitButton
              text="Contact Sales"
              size="SM"
              className="button button-secondary"
              href="plans/contact-sales"
            />
          :
            <SubmitButton
              text="Downgrade"
              size="SM"
              className="button button-secondary"
              disabled={ user?.isBlocked || user?.hasPaymentIssues }
              fetchersToCheck={ [] }
              href={ "plans/downgrade/" + smallerPlan.planSlug }
              tooltip={ user?.hasPaymentIssues ? "Resolve outstanding invoices before changing your plan" : undefined }
            />
        }
      />
    );
  }

  return null;
}

function CancellationSection() {
  const user = useUser();
  const { activeSubscription, activeSubscriptionFetcher } = useActiveSubscription();
  const { flowType } = useBillingFlow();

  if (!activeSubscriptionFetcher || (flowType === "free")) {
    return null;
  }

  // TODO the cancel section had an extra, suggesting the user to add a payment method if they don't have any
  //  don't think that's necessary, users should be able to "keep their plans" without adding a payment method.
  //  they will just face the consequences on the next billing date.
  if (activeSubscription?.scheduledSubscription?.planSlug === "free") {
    // cancellation already scheduled, let users change their minds
    return (
      <BillingDetailsCard
        title="Cancellation scheduled"
        subtitle={
          "Your subscription will be cancelled on " + fmtDate(fromEpochSeconds(activeSubscription.currentTermEnd)) + ". " +
          (user?.isBannedFromFree ? "Your API key will be removed." : "You will move to the Free plan.")
        }
        extra={
          <activeSubscriptionFetcher.Form
            method="POST"
            action={ ActiveSubscriptionDataRoutePath }
          >
            <SubmitButton
              text="Keep my plan"
              size="SM"
              className="button button-secondary"
              disabled={ user?.isBlocked }
              fetchersToCheck={ [ activeSubscriptionFetcher ] }
              { ...ActiveSubscriptionFormActions.remove_scheduled_changes }
            />
          </activeSubscriptionFetcher.Form>
        }
      />
    );
  }

  const isEnterprise = flowType === "slg";

  return (
    <BillingDetailsCard
      title="Cancel subscription"
      subtitle={
        isEnterprise ?
          "Contact our sales team to discuss cancellation options." :
          "Cancelling your subscription moves you to the Free plan" // TODO this is not quite true, in case of 'banned from free' it's a blocking
      }
      extra={
        <SubmitButton
          text={ isEnterprise ? "Contact Sales" : "Cancel Subscription" }
          size="SM"
          className="button button-secondary"
          disabled={ user?.isBlocked }
          fetchersToCheck={ [] }
          href={ isEnterprise ? "plans/contact-sales" : "plans/cancel" }  // TODO current cancellation dialog has poor navigation and will put users back on the Billing/Subscription tab
          onClick={ () => trackBillingV2CancelClicked() }
        />
      }
    />
  );
}

function DeleteAccountSection() {
  const user = useUser();

  return (
    <BillingDetailsCard
      title="Delete Account"
      subtitle="Permanently delete your account and all associated data. This action cannot be undone."
      extra={
        <SubmitButton
          text="Delete Account"
          size="SM"
          className="button button-secondary destructive"
          href="plans/manage-plan/delete-account"
          disabled={ user?.hasPaymentIssues || user?.isPendingDeletion || user?.isBlocked }
          fetchersToCheck={ [] }
        />
      }
    />
  );
}

export default function ManagePlanActionsCard() {
  const { activeSubscriptionFetcher } = useActiveSubscription();

  useToasterFromActionData(activeSubscriptionFetcher);

  return (
    <CardStack horizontal={ false }>
      <DowngradeSection />
      <CancellationSection />
      <DeleteAccountSection />
    </CardStack>
  );
};
